<template>
  <section class="sos">
    <div class="sos-top">
      <div class="sos-form">
        <div class="sos-form-item">
          <span class="sos-form-item-title">{{ t('geopoint') }}</span>
          <select v-model="location" class="sos-form-item-input form-select">
            <option :value="null" disabled>{{ t('select_placeholder') }}</option>
            <option :value="1">{{ t('locationList.1') }}</option>
            <option :value="2">{{ t('locationList.2') }}</option>
          </select>
        </div>

        <div class="sos-form-item">
          <span class="sos-form-item-title">{{ t('message') }}</span>
          <textarea
            v-model="text"
            rows="4"
            class="sos-form-item-input sos-form-item-textarea"
            :placeholder="t('textarea_placeholder')"
          ></textarea>
        </div>

        <div class="sos-form-item">
          <span class="sos-form-item-title">{{ t('phone_number_label') }}</span>
          <input
            v-model="phone"
            type="text"
            class="sos-form-item-input form-control"
            :placeholder="t('phone_number_placeholder')"
            @keypress="onlyDigits"
          />
        </div>

        <div
          class="sos-form-item"
          @click="openFileDialog"
          @dragover.prevent="isDragover = true"
          @dragleave.prevent="isDragover = false"
          @drop.prevent="onDrop"
          :class="{ dragover: isDragover }"
        >
          <span class="sos-form-item-title">{{ t('addFile') }}</span>
          <input
            ref="fileInputRef"
            type="file"
            class="d-none"
            multiple
            accept="image/png, image/jpeg, image/jpg, image/svg+xml"
            @change="onFileSelect"
          />

          <div class="sos-files">
            <span class="sos-files-title">{{ t('addOrDropFile') }}</span>
            <span class="sos-files-text">{{ t('fileTypes') }}</span>
          </div>

          <div v-if="files.length" class="file-list">
            <div class="file-item" v-for="(file, index) in files" :key="`${file.name}-${index}`">
              {{ file.name }} ({{ (file.size / 1024 / 1024).toFixed(1) }} MB)
              <button class="file-remove" type="button" @click.stop="removeFile(index)">x</button>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="error-box">{{ errorMessage }}</div>

        <button class="submit-btn" :disabled="isSubmitting || isUploading" @click="sendRequest">
          {{ t('send_sos') }}
        </button>
      </div>

      <div class="sos-image">
        <img :src="sosImage" alt="sos" />
      </div>
    </div>

    <div class="sos-bottom">
      <span class="sos-bottom-title">{{ t('needHelp') }}</span>
      <div class="help-list">
        <div class="help-item" v-for="item in helpItems" :key="item.phone" @click="openInCall(item.phone)">
          <div class="help-item-top">
            <span class="help-item-top-phone">{{ item.phone }}</span>
            <img :src="phoneIcons[item.icon]" :alt="item.phone" />
          </div>
          <div class="help-item-text">{{ t(item.labelKey) }}</div>
        </div>
      </div>
    </div>

    <div v-if="successModalVisible" class="success-modal-backdrop" @click.self="successModalVisible = false">
      <div class="success-modal">
        <span class="material-icons-outlined success-icon">check_circle</span>
        <p class="success-title">{{ t('sosModalTitle') }}</p>
        <p class="success-description">{{ t('sosModalText') }}</p>
        <button class="success-close" type="button" @click="successModalVisible = false">{{ t('close') }}</button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import sosImage from '@/assets/sos/sos.svg';
import icon101 from '@/assets/sos/phones/101.png';
import icon102 from '@/assets/sos/phones/102.png';
import icon103 from '@/assets/sos/phones/103.png';
import icon104 from '@/assets/sos/phones/104.png';
import icon111 from '@/assets/sos/phones/111.png';
import icon112 from '@/assets/sos/phones/112.png';
import icon115 from '@/assets/sos/phones/115.png';
import icon150 from '@/assets/sos/phones/150.png';
import icon1424 from '@/assets/sos/phones/1424.png';
import { useSendSosMessageMutation, useUploadSosFileMutation } from '@/queries';
import { useTopbarStore } from '@/stores/topbar';

const { t } = useI18n();
const router = useRouter();
const topbarStore = useTopbarStore();
const sendSosMutation = useSendSosMessageMutation();
const uploadFileMutation = useUploadSosFileMutation();

const fileInputRef = ref(null);
const location = ref(null);
const text = ref('');
const phone = ref('');
const files = ref([]);
const isDragover = ref(false);
const successModalVisible = ref(false);
const errorMessage = ref('');

const phoneIcons = {
  '101.png': icon101,
  '102.png': icon102,
  '103.png': icon103,
  '104.png': icon104,
  '111.png': icon111,
  '112.png': icon112,
  '115.png': icon115,
  '150.png': icon150,
  '1424.png': icon1424
};

const helpItems = [
  { phone: '101', icon: '101.png', labelKey: 'helpList.101' },
  { phone: '102', icon: '102.png', labelKey: 'helpList.102' },
  { phone: '103', icon: '103.png', labelKey: 'helpList.103' },
  { phone: '104', icon: '104.png', labelKey: 'helpList.104' },
  { phone: '111', icon: '111.png', labelKey: 'helpList.111' },
  { phone: '112', icon: '112.png', labelKey: 'helpList.112' },
  { phone: '115', icon: '115.png', labelKey: 'helpList.115' },
  { phone: '150', icon: '150.png', labelKey: 'helpList.150' },
  { phone: '1424', icon: '1424.png', labelKey: 'helpList.1424' }
];

const isSubmitting = computed(() => sendSosMutation.isPending.value);
const isUploading = computed(() => uploadFileMutation.isPending.value);

function onlyDigits(event) {
  if (event.key === '+' && phone.value.length === 0) return;
  if (/[0-9]/.test(event.key)) return;
  event.preventDefault();
}

function openInCall(phoneNumber) {
  window.open(`tel:${phoneNumber}`, '_blank');
}

function openFileDialog() {
  fileInputRef.value?.click();
}

function onFileSelect(event) {
  handleFiles(event.target.files);
  event.target.value = '';
}

function onDrop(event) {
  isDragover.value = false;
  handleFiles(event.dataTransfer.files);
}

function removeFile(index) {
  files.value.splice(index, 1);
}

function normalizeError(error, fallbackKey = 'errorTextSend') {
  return error?.response?.data?.message || error?.message || t(fallbackKey);
}

async function handleFiles(fileList) {
  let incoming = Array.from(fileList || []);
  errorMessage.value = '';

  if (files.value.length + incoming.length > 3) {
    errorMessage.value = t('max3Files');
    return;
  }

  incoming = incoming.filter((file) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      errorMessage.value = t('fileFormatNotSupported').replace('{name}', file.name);
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      errorMessage.value = t('sizeExceeded').replace('{name}', file.name);
      return false;
    }
    return true;
  });

  for (const file of incoming) {
    try {
      const uploaded = await uploadFileMutation.mutateAsync(file);
      files.value.push({
        ...uploaded,
        name: file.name,
        size: file.size
      });
    } catch (error) {
      errorMessage.value = normalizeError(error);
      return;
    }
  }
}

async function sendRequest() {
  errorMessage.value = '';
  if (!location.value) {
    errorMessage.value = t('selectGeolocation');
    return;
  }
  if (!text.value?.trim()) {
    errorMessage.value = t('writeText');
    return;
  }

  const payload = {
    location: location.value,
    text: text.value.trim(),
    phone: phone.value?.trim() || '',
    files: files.value.map((file) => file.url).filter(Boolean)
  };

  try {
    await sendSosMutation.mutateAsync(payload);
    location.value = null;
    text.value = '';
    phone.value = '';
    files.value = [];
    successModalVisible.value = true;
  } catch (error) {
    errorMessage.value = normalizeError(error);
  }
}

onMounted(() => {
  topbarStore.setTitle(t('sos'));
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => router.push('/feed')
  });
});
</script>

<style scoped>
.sos {
  padding-top: 32px;
  padding-inline: 24px;
  gap: 24px;
  display: flex;
  flex-direction: column;
}

.sos-top {
  display: flex;
  gap: 24px;
}

.sos-image {
  display: none;
}

@media screen and (min-width: 768px) {
  .sos-image {
    display: flex;
    align-items: center;
  }
}

.sos-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sos-form-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sos-form-item-title {
  font-weight: 600;
  font-size: 14px;
  color: #49494d;
}

.sos-form-item-input {
  border-color: #d9dbe1;
}

.sos-form-item-textarea {
  border-radius: 4px;
  padding-left: 12px;
  padding-top: 4px;
}

.sos-files {
  border-radius: 8px;
  border: 1.4px dashed #d9dbe1;
  background: #f5f6f7;
  height: 91px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.dragover .sos-files {
  border-color: #4a90e2;
  background-color: rgba(74, 144, 226, 0.1);
}

.sos-files-title {
  font-weight: 600;
  font-size: 14px;
  color: #49494d;
  padding-inline: 12px;
  text-align: center;
}

.sos-files-text {
  font-size: 12px;
  color: #787878;
  text-align: center;
}

.file-list {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.file-item {
  background: #f5f6f7;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.file-remove {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #f14635;
  font-weight: 700;
}

.error-box {
  color: #f14635;
  font-size: 13px;
}

.submit-btn {
  min-height: 40px;
  border: none;
  border-radius: 4px;
  color: #fff;
  background: #f14635;
  font-weight: 600;
}

.submit-btn:disabled {
  opacity: 0.6;
}

.sos-bottom {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.sos-bottom-title {
  font-weight: 600;
  font-size: 32px;
  color: #262628;
}

.help-list {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.help-item {
  width: 100%;
  max-width: 180px;
  border: 1px solid #e5e5e5;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
}

.help-item-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
}

.help-item-top-phone {
  font-size: 32px;
  color: #091c44;
}

.help-item-text {
  background: #f1463533;
  border-top: 1px solid #e5e5e5;
  font-size: 14px;
  color: #091c44;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 8px;
}

@media screen and (max-width: 768px) {
  .sos {
    padding-inline: 12px;
  }

  .sos-bottom-title {
    font-size: 20px;
    line-height: 24px;
  }

  .help-item {
    flex: 1 1 calc(50% - 10px);
    max-width: unset;
  }
}

.success-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
}

.success-modal {
  background: #fff;
  border-radius: 8px;
  width: min(460px, calc(100vw - 24px));
  padding: 24px;
  text-align: center;
}

.success-icon {
  color: #52c41a;
  font-size: 72px;
}

.success-title {
  font-weight: 500;
  font-size: 20px;
  margin-bottom: 8px;
}

.success-description {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 20px;
}

.success-close {
  border: none;
  background: transparent;
  color: #2f80ed;
  font-size: 18px;
}
</style>
